import { faPen, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { Activity, ActivityType, HydrationIntake, ReliefBucket, RestValue } from '@spike/activity-model';
import {
    ACTIVITY_ICON_COLORS_MAP,
    ACTIVITY_ICON_FOREGROUND_COLORS_MAP,
    ACTIVITY_ICON_MAP,
    ACTIVITY_TITLE_MAP,
    ActivityIcon,
    HYDRATION_LABELS,
    RELIEF_CATEGORY_LABELS,
    RELIEF_SLOTS,
    REST_LABELS
} from 'components/Activities/Icon/ActivityIcon';
import styles from 'components/Activities/Timeline/Item/ActivityTimelineItem.module.scss';
import { MediaGalleryGrid } from 'components/UI/V3/MediaGalleryGrid/MediaGalleryGrid';
import { Media, MediaViewer } from 'components/UI/V3/MediaViewer/MediaViewer';
import { GenericDeleteModal } from 'components/UI/V3/Modals/DeleteModal';
import { TimelineItemAction } from 'components/UI/V3/Timeline/Item/Action/TimelineItemAction';
import { TimelineItemDivider } from 'components/UI/V3/Timeline/Item/Divider/TimelineItemDivider';
import { TimelineItemHeader } from 'components/UI/V3/Timeline/Item/Header/TimelineItemHeader';
import { TimelineItemMeta } from 'components/UI/V3/Timeline/Item/Meta/TimelineItemMeta';
import { TimelineItem } from 'components/UI/V3/Timeline/Item/TimelineItem';
import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useActivityTimeline } from '../ActivityTimelineProvider';

interface ActivityTimelineItemProps {
    activity: Activity;
    showActions?: boolean;
}

export const ActivityTimelineItem: React.FC<ActivityTimelineItemProps> = ({ activity, showActions = true }) => {
    const { isLoading, isPetEnabled, deleteActivity, openActivityForm, zIndex } = useActivityTimeline();
    const activitySubtypes = useSelector((state: RootState) => state.masterData.data.activitySubtypes);
    const masterData = useSelector((state: RootState) => state.masterData.data);
    const staffMembers = useSelector((state: RootState) => state.staff.staff);
    const isReadOnly = !showActions;

    const [isMediaViewerOpen, setIsMediaViewerOpen] = React.useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
    const activityType = activity.type;

    const icon = React.useMemo(() => ACTIVITY_ICON_MAP[activityType], [activityType]);
    const iconColor = React.useMemo(() => ACTIVITY_ICON_COLORS_MAP[activityType], [activityType]);
    const iconForegroundColor = React.useMemo(() => ACTIVITY_ICON_FOREGROUND_COLORS_MAP[activityType], [activityType]);

    const media = React.useMemo<Array<Media>>(
        () => [
            ...activity.photos.map(photo => ({
                type: 'image' as const,
                src: photo.url,
                thumbnailUrl: photo.thumbnailUrl
            })),
            ...activity.videos.map(video => ({
                type: 'video' as const,
                src: video.url
            }))
        ],
        [activity.photos, activity.videos]
    );

    const subtypeLabelById = React.useMemo(() => {
        const options =
            activityType === ActivityType.INCIDENT
                ? activitySubtypes?.incident ?? []
                : activityType === ActivityType.ENRICHMENT
                ? activitySubtypes?.enrichment ?? []
                : [];

        return new Map(options.map(option => [option.id, option.name]));
    }, [activityType, activitySubtypes]);

    const activitySubtypeLabels = React.useMemo(
        () =>
            (activity.activitySubtype ?? [])
                .filter((subtype): subtype is string => typeof subtype === 'string')
                .map(subtypeId => subtypeLabelById.get(subtypeId) ?? subtypeId),
        [activity.activitySubtype, subtypeLabelById]
    );

    const singleSubtypeLabel = React.useMemo(() => {
        const value = activity.activitySubtype?.[0];

        if (typeof value !== 'string') {
            return '';
        }

        if (activityType === ActivityType.HYDRATION) {
            return HYDRATION_LABELS[value as HydrationIntake] ?? value;
        }

        if (activityType === ActivityType.REST) {
            return REST_LABELS[value as RestValue] ?? value;
        }

        return '';
    }, [activity.activitySubtype, activityType]);

    const reliefBuckets = React.useMemo<Array<ReliefBucket>>(
        () =>
            activityType === ActivityType.RELIEFS
                ? (activity.activitySubtype as Array<ReliefBucket> | undefined) ?? []
                : [],
        [activityType, activity.activitySubtype]
    );

    const activityInvolvedStaffs = (
        activity as Activity & { involvedStaffs?: Array<{ id: number; name: string; image?: string }> }
    ).involvedStaffs;

    const staffById = React.useMemo(() => {
        return new Map(
            staffMembers
                .filter(member => member.id)
                .map(member => [
                    member.id as number,
                    {
                        name: `${member.person.firstName} ${member.person.lastName}`,
                        image: member.person.avatar ?? undefined
                    }
                ])
        );
    }, [staffMembers]);

    const involvedStaffChips = React.useMemo(() => {
        if (activityInvolvedStaffs && activityInvolvedStaffs.length > 0) {
            return activityInvolvedStaffs.map(staff => ({
                id: staff.id,
                name: staff.name,
                image: staff.image ?? staffById.get(staff.id)?.image
            }));
        }

        return (activity.involvedStaffIds ?? []).map(staffId => ({
            id: staffId,
            name: staffById.get(staffId)?.name ?? `#${staffId}`,
            image: staffById.get(staffId)?.image
        }));
    }, [activityInvolvedStaffs, activity.involvedStaffIds, staffById]);

    const actions = React.useMemo(() => {
        if (!showActions) {
            return undefined;
        }

        return (
            <>
                <TimelineItemAction
                    icon={faTrashAlt}
                    onClick={() => setIsDeleteModalOpen(true)}
                />
                {isPetEnabled && (
                    <TimelineItemAction
                        icon={faPen}
                        onClick={() => openActivityForm(undefined, activity)}
                    />
                )}
            </>
        );
    }, [showActions, isPetEnabled, activity, openActivityForm]);

    const activityTitle = React.useMemo(() => {
        if (activityType === ActivityType.MEDICATION && activity.medicationMethodId != null) {
            const method = masterData.medicationMethodTypes?.find(m => m.id === activity.medicationMethodId);
            const unit = masterData.medicationDosageUnits?.find(u => u.id === activity.dosageUnitId);

            if (method && activity.dosageAmount != null && unit) {
                return `Medication · ${method.name} ${activity.dosageAmount} ${unit.name}`;
            }
        }

        return ACTIVITY_TITLE_MAP[activityType] ?? activityType;
    }, [activityType, activity, masterData]);

    const ateLabel = React.useMemo(() => {
        if (activityType !== ActivityType.FEEDING || !activity.ate) return null;
        return masterData.feedingAteValues?.find(v => v.id === activity.ate)?.name ?? null;
    }, [activityType, activity.ate, masterData]);

    const hasNotes = !!activity.note;

    return (
        <>
            <TimelineItem
                icon={icon}
                iconColor={iconColor}
                iconForegroundColor={iconForegroundColor}
                dateTime={activity.date}
                className={styles.root}
            >
                <TimelineItemHeader
                    title={activityTitle}
                    actions={actions}
                />

                {activityType === ActivityType.FEEDING && (
                    <>
                        <div className={styles.metaGrid}>
                            <TimelineItemMeta
                                title="Completed by"
                                description={activity.staffName}
                            />
                            <TimelineItemMeta
                                title="Date & Time"
                                description={activity.date.format('MMM D, YYYY h:mma')}
                            />
                            <TimelineItemMeta
                                title="Ate"
                                description={ateLabel ?? '–'}
                            />
                        </div>
                        {hasNotes && (
                            <TimelineItemMeta
                                title="Notes"
                                description={activity.note}
                            />
                        )}
                    </>
                )}

                {activityType === ActivityType.MEDICATION && (
                    <>
                        <TimelineItemMeta
                            title="Completed by"
                            description={activity.staffName}
                        />
                        {hasNotes && (
                            <TimelineItemMeta
                                title="Notes"
                                description={activity.note}
                            />
                        )}
                    </>
                )}

                {activityType === ActivityType.INCIDENT && (
                    <>
                        <TimelineItemMeta
                            title="Pets"
                            description={activity.pets.map(pet => pet.name).join(', ') || 'None selected'}
                        />

                        <TimelineItemMeta
                            title="Incident Type"
                            description={activitySubtypeLabels.join(', ')}
                        />

                        {involvedStaffChips.length > 0 && (
                            <TimelineItemMeta title="Staff Involved">
                                <div className={styles.staffChips}>
                                    {involvedStaffChips.map((staff, index) => (
                                        <span
                                            key={`incident-staff-${index}-${staff.id}`}
                                            className={styles.staffChip}
                                        >
                                            {staff.image && (
                                                <img
                                                    className={styles.staffAvatar}
                                                    src={staff.image}
                                                    alt={staff.name}
                                                />
                                            )}
                                            {staff.name}
                                        </span>
                                    ))}
                                </div>
                            </TimelineItemMeta>
                        )}

                        {hasNotes && (
                            <TimelineItemMeta
                                title="Incident Description"
                                description={activity.note}
                            />
                        )}
                    </>
                )}

                {activityType === ActivityType.ENRICHMENT && (
                    <>
                        <TimelineItemMeta
                            title="Activity Type"
                            description={activitySubtypeLabels.join(', ')}
                        />

                        {involvedStaffChips.length > 0 && (
                            <TimelineItemMeta title="Staff">
                                <div className={styles.staffChips}>
                                    {involvedStaffChips.map((staff, index) => (
                                        <span
                                            key={`enrichment-staff-${index}-${staff.id}`}
                                            className={styles.staffChip}
                                        >
                                            {staff.image && (
                                                <img
                                                    className={styles.staffAvatar}
                                                    src={staff.image}
                                                    alt={staff.name}
                                                />
                                            )}
                                            {staff.name}
                                        </span>
                                    ))}
                                </div>
                            </TimelineItemMeta>
                        )}

                        {hasNotes && (
                            <TimelineItemMeta
                                title="Activity Notes"
                                description={activity.note}
                            />
                        )}
                    </>
                )}

                {activityType === ActivityType.HYDRATION && (
                    <TimelineItemMeta
                        title="Water Intake"
                        description={singleSubtypeLabel}
                    />
                )}

                {activityType === ActivityType.REST && (
                    <TimelineItemMeta
                        title="Have they rested?"
                        description={singleSubtypeLabel}
                    />
                )}

                {activityType === ActivityType.RELIEFS &&
                    reliefBuckets.map((bucket, index) => (
                        <div
                            key={`relief-${bucket.type}-${index}`}
                            className={styles.reliefBlock}
                        >
                            <p className={styles.reliefTitle}>{RELIEF_CATEGORY_LABELS[bucket.type] ?? bucket.type}</p>

                            <div className={styles.reliefTable}>
                                <div className={styles.reliefTableHeader}>
                                    <span>Time of Day</span>
                                    <span>How Many Times?</span>
                                </div>
                                {RELIEF_SLOTS.map(slot => ({ label: slot.label, count: bucket[slot.key] }))
                                    .filter(({ count }) => count !== null && count !== undefined)
                                    .map(({ label, count }) => (
                                        <div
                                            key={label}
                                            className={styles.reliefTableRow}
                                        >
                                            <span>{label}</span>
                                            <span>{count}</span>
                                        </div>
                                    ))}
                            </div>

                            {bucket.note && (
                                <TimelineItemMeta
                                    title="Notes"
                                    description={bucket.note}
                                />
                            )}
                        </div>
                    ))}

                {activity.photos.length > 0 && (
                    <>
                        {[ActivityType.INCIDENT, ActivityType.ENRICHMENT].includes(activityType) && (
                            <TimelineItemDivider />
                        )}

                        <MediaGalleryGrid
                            media={activity.photos.map(photo => ({
                                type: 'image',
                                url: photo.thumbnailUrl
                            }))}
                            className={styles.gallery}
                            onClick={() => setIsMediaViewerOpen(true)}
                        />
                    </>
                )}

                {activity.videos.length > 0 && (
                    <MediaGalleryGrid
                        media={activity.videos.map(video => ({
                            type: 'video',
                            url: video.url
                        }))}
                        className={styles.gallery}
                        onClick={() => setIsMediaViewerOpen(true)}
                    />
                )}

                {hasNotes &&
                    ![
                        ActivityType.INCIDENT,
                        ActivityType.ENRICHMENT,
                        ActivityType.FEEDING,
                        ActivityType.MEDICATION,
                        ActivityType.RELIEFS
                    ].includes(activityType) && (
                        <>
                            <TimelineItemDivider />

                            <TimelineItemMeta
                                title={activityType === ActivityType.INCIDENT ? 'Incident Description' : 'Notes'}
                                description={activity.note}
                            />
                        </>
                    )}
            </TimelineItem>

            {isMediaViewerOpen && (
                <MediaViewer
                    media={media}
                    zIndex={zIndex ? zIndex + 100 : undefined}
                    onClose={() => setIsMediaViewerOpen(false)}
                >
                    <div className={styles.mediaViewerHeader}>
                        <ActivityIcon type={activityType} />

                        <div className={styles.mediaViewerHeaderMeta}>
                            <p>
                                <b>{ACTIVITY_TITLE_MAP[activityType] ?? activityType}</b>
                            </p>
                            <p>
                                {activity.date.format('ddd, DD MMM YYYY [at] hh:mma')} · {activity.staffName}
                            </p>
                        </div>
                    </div>

                    {hasNotes && (
                        <div className={styles.mediaViewerNote}>
                            <p>{activity.note}</p>
                        </div>
                    )}
                </MediaViewer>
            )}

            {isDeleteModalOpen && (
                <GenericDeleteModal
                    isLoading={isLoading}
                    className={styles.deleteModal}
                    zIndex={zIndex}
                    title={<div className={styles.deleteModalTitle}>Delete Activity</div>}
                    body={<div className={styles.deleteModalBody}>Are you sure you want to delete this activity?</div>}
                    onConfirm={() => !isReadOnly && deleteActivity(activity)}
                    onCancel={() => setIsDeleteModalOpen(false)}
                />
            )}
        </>
    );
};
